import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { articles, featuredVideos } from '@/mocks/douhuaData';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId);
  const relatedVideos = featuredVideos.slice(0, 4);
  const relatedArticles = articles.filter((a) => a.id !== articleId).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 md:pt-24 pb-12">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <ArticleSEO article={article} />
      <Navbar />

      <main className="pt-20 md:pt-24 pb-12">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: article.category, to: `/category/${articles[0] ? 'movie' : 'movie'}` },
              { label: article.title },
            ]} />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full whitespace-nowrap">{article.category}</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">阅读 {article.readTime}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">作者 {article.author}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">{article.title}</h1>
            <div className="w-full h-56 md:h-80 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </header>

          {/* Article Content */}
          <div className="prose-custom max-w-none mb-12">
            {article.content.split('\n').filter(Boolean).map((paragraph, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Article Footer */}
          <div className="border-t border-background-200 pt-6 mb-12">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-400">标签：</span>
              <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">豆花视频</span>
              <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">免费在线观看</span>
              <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">影视推荐</span>
            </div>
          </div>

          {/* Related Videos */}
          <section className="mb-12">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">推荐观看</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
              {relatedVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={video.image}
                      alt={video.title}
                      title={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md whitespace-nowrap">
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">{video.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          <section>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group flex flex-col sm:flex-row gap-3 p-4 bg-background-100 rounded-xl border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="w-full sm:w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-accent-600 mb-1">{rel.category}</p>
                    <p className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">{rel.title}</p>
                    <p className="text-xs text-foreground-400 mt-1">{rel.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </>
  );
}

function ArticleSEO({ article }: { article: typeof articles[0] }) {
  useDocumentHead({
    title: `${article.title} - 豆花视频`,
    description: article.excerpt,
    keywords: `豆花视频,${article.category},免费在线观看,影视资讯`,
    canonicalUrl: `https://douhua.example.com/article/${article.id}`,
  });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '豆花视频',
    },
  };

  return (
    <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
  );
}